import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import MoreButtonCDS from 'app/shared/componentsRestyled/coreDesignSystem/Buttons/MoreButtonCDS';
import {
  DeployIcon,
  FileSettingsIcon,
  NoteIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import MenuPopoverCDS from 'app/shared/componentsRestyled/coreDesignSystem/MenuPopoverCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import usePopover from 'hooks/usePopover';

import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';
import {
  selectIsLoanLenderEngineType,
  selectIsLoanOptimalBlueOrSifterEngineType,
  selectIsPriceConcessionAllowed,
} from 'store/loanApplications/selectors';

import { CustomAdjustmentType } from './LockInfo.constants';

const AddActionButton = ({
  onClick,
  onAddFloatDown,
  isDisabled = false,
  sessionInfo = null,
  hasCustomAdjustmentsPendingChanges = false,
}) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);

  const isLenderEngineType = useSelector(selectIsLoanLenderEngineType);
  const isOptimalBlueOrSifterEngineType = useSelector(selectIsLoanOptimalBlueOrSifterEngineType);
  const isPriceConcessionAllowed = useSelector(selectIsPriceConcessionAllowed);

  const isCustomAdjustmentsEditAllowed = useSelector(
    selectIsClaimAllowed(PAGE_CODES.CUSTOM_PRICE_ADJUSTMENT),
  );
  const { isEditAllowed: isFloatDownEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.FLOAT_DOWN),
  );
  const { isEditAllowed: isLockExtensionsEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOCK_EXTENSIONS),
  );
  const hasLockExtensionOverrideClaim = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOCK_EXTENSIONS_OVERRIDE),
  );

  const isLockExtensionOverrideAllowed = hasLockExtensionOverrideClaim;

  const isOBExternalLoanLock = isOptimalBlueOrSifterEngineType && isExternalLoanLockEnabled;

  const isCustomAdjustmentsDisabled =
    !isCustomAdjustmentsEditAllowed ||
    (!isLenderEngineType && !(isOBExternalLoanLock && isPriceConcessionAllowed)) ||
    (!!sessionInfo?.type && sessionInfo.type !== CustomAdjustmentType.CUSTOM_ADJUSTMENT) ||
    (isOBExternalLoanLock && hasCustomAdjustmentsPendingChanges);

  const isFloatDownDisabled =
    !isFloatDownEditAllowed ||
    !isLenderEngineType ||
    (!!sessionInfo?.type && sessionInfo.type !== CustomAdjustmentType.FLOAT_DOWN) ||
    (!!sessionInfo?.type &&
      sessionInfo.type === CustomAdjustmentType.FLOAT_DOWN &&
      sessionInfo.addedItems.length > 0);

  const isLockExtensionDisabled =
    (!isLockExtensionsEditAllowed && !isLockExtensionOverrideAllowed) ||
    (!isLenderEngineType && !isOBExternalLoanLock) ||
    (!!sessionInfo?.type && sessionInfo.type !== CustomAdjustmentType.EXTENSION) ||
    (!!sessionInfo?.type &&
      sessionInfo.type === CustomAdjustmentType.EXTENSION &&
      sessionInfo.addedItems.length > 0);

  const items = useMemo(
    () => [
      {
        text: 'Custom Adjustment',
        'data-actiontype': CustomAdjustmentType.CUSTOM_ADJUSTMENT,
        icon: <FileSettingsIcon size={IconSize[20]} />,
        onClick,
        isDisabled: isCustomAdjustmentsDisabled || isDisabled,
      },
      {
        text: 'Float Down Option',
        'data-actiontype': CustomAdjustmentType.FLOAT_DOWN,
        icon: <NoteIcon size={IconSize[20]} />,
        onClick: onAddFloatDown,
        isDisabled: isFloatDownDisabled || isDisabled,
      },
      {
        text: 'Lock Extension',
        'data-actiontype': CustomAdjustmentType.EXTENSION,
        icon: <DeployIcon size={IconSize[20]} />,
        onClick,
        isDisabled: isLockExtensionDisabled || isDisabled,
      },
    ],
    [
      onClick,
      isCustomAdjustmentsDisabled,
      isDisabled,
      onAddFloatDown,
      isFloatDownDisabled,
      isLockExtensionDisabled,
    ],
  );

  return (
    <>
      <MoreButtonCDS
        disabled={_.every(items, i => i.isDisabled)}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

AddActionButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  onAddFloatDown: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  sessionInfo: PropTypes.object,
  hasCustomAdjustmentsPendingChanges: PropTypes.bool,
};

export default AddActionButton;
